import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { useGuestAuth } from '../context/GuestAuthContext';
import { guestAPI } from '../services/api';
import { toast } from 'sonner';

/**
 * Full-screen phone verification gate shown to every visitor who hasn't
 * verified yet. Blocks all public routes until they receive an OTP and
 * enter the code. Skipped for admin routes.
 *
 * Two-step:
 *  1. first_name + phone number → tap "Send code" → Twilio SMS.
 *  2. 6-digit code → tap "Verify" → localStorage session (30 days).
 */
const PhoneGate = () => {
  const { isVerified, ready, saveSession } = useGuestAuth();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (!ready) return null;
  if (isVerified) return null;
  if (isAdminRoute) return null;

  const handleSend = async (e) => {
    e.preventDefault();
    if (!firstName.trim()) return toast.error('Please enter your first name.');
    if (!phone.trim()) return toast.error('Please enter your phone number.');
    setSending(true);
    try {
      await guestAPI.sendOtp(firstName.trim(), phone.trim());
      toast.success('Verification code sent to your phone.');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send code. Try again.');
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.trim().length < 4) {
      return toast.error('Please enter the code from the SMS.');
    }
    setVerifying(true);
    try {
      const data = await guestAPI.verifyOtp(firstName.trim(), phone.trim(), code.trim());
      saveSession(data);
      toast.success(`Welcome, ${data.first_name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-background/90 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-card border border-border rounded-3xl shadow-[0_40px_80px_-30px_rgba(45,42,38,0.35)] p-8 sm:p-10">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <h2 className="font-serif text-3xl text-foreground">Welcome in</h2>
          <p className="text-sm text-muted-foreground mt-2 font-light max-w-xs">
            {step === 1
              ? 'Tell us who you are — takes 20 seconds.'
              : `We just sent a 6-digit code to ${phone}.`}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                First name
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Anaya"
                className="w-full h-12 px-4 rounded-full bg-background border border-border text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="gate-first-name"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full h-12 px-4 rounded-full bg-background border border-border text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="gate-phone"
              />
              <p className="text-xs text-muted-foreground/80 mt-2 font-light">
                We&apos;ll send you a one-time code by SMS. Indian numbers add automatically; for others, prefix with country code.
              </p>
            </div>
            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-primary text-primary-foreground text-sm font-medium tracking-wide transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_18px_40px_-12px_rgba(179,74,62,0.45)] disabled:opacity-60 disabled:hover:scale-100"
              data-testid="gate-send-btn"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
              ) : (
                <>Send code <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                Verification code
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="123456"
                className="w-full h-14 px-4 rounded-full bg-background border border-border text-foreground text-center text-2xl tracking-[0.5em] font-serif focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid="gate-code"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={verifying}
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-primary text-primary-foreground text-sm font-medium tracking-wide transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_18px_40px_-12px_rgba(179,74,62,0.45)] disabled:opacity-60 disabled:hover:scale-100"
              data-testid="gate-verify-btn"
            >
              {verifying ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Verifying...</>
              ) : (
                <>Verify & enter <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="gate-change-number-btn"
            >
              ← use a different number
            </button>
          </form>
        )}

        <p className="mt-8 text-[11px] text-muted-foreground/70 text-center font-light leading-relaxed">
          By continuing you agree to receive an SMS from Red Panda Socials. Standard carrier rates may apply.
        </p>
      </div>
    </div>
  );
};

export default PhoneGate;
